import { GridColDef } from "@mui/x-data-grid-pro";
import formatDate from "date-fns/format";
import {
  ADJUSTMENT_TYPE_LABEL,
  AIRPORT_TRIP_FEE_AMOUNT_COLUMN,
  formatReferenceTransactionIndex,
  getAdjustmentDescription,
  isAdjustment,
} from "../../common";
import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";
import { convertDateToString, convertTimeToString, formatDurationMs } from "common/format/time";
import { getSessionEndDate, getSessionStartDate, SessionTimeRow } from "../getSessionDates";
import { WALLET_TRANSACTION_TYPE_LABELS } from "./walletTransactionTypeLabels";

type ExportSessionRow = SessionTimeRow & Pick<CurbsuiteTransactionLogEntry, "transactionType">;

// An adjustment (AUT-6163) is a ledger correction, not a session: its Event Start/End cells are
// blank rather than the 1970 epoch getSessionStartDate/EndDate fall back to.
const exportSessionStart = (row: ExportSessionRow): Date | null =>
  isAdjustment(row) ? null : getSessionStartDate(row);
const exportSessionEnd = (row: ExportSessionRow): Date | null => (isAdjustment(row) ? null : getSessionEndDate(row));
const formatOrBlank =
  (format: (date: Date) => string) =>
  (params: { value: unknown }): string =>
    params.value ? format(params.value as Date) : "";

/** The on-screen columns that already carry a row's type; when one is present the CSV has a Type. */
const TYPE_COLUMN_FIELDS = ["transactionType", "busStationTransactionType"];

const ADJUSTMENT_REFERENCE_COLUMN = {
  field: "adjustmentReference",
  headerName: "Reference #",
  sortable: false,
  filterable: false,
  valueGetter: ({ row }: { row: CurbsuiteTransactionLogEntry }) =>
    isAdjustment(row) ? formatReferenceTransactionIndex(row) ?? "" : "",
} as GridColDef<CurbsuiteTransactionLogEntry>;

const ADJUSTMENT_DESCRIPTION_COLUMN = {
  field: "adjustmentDescription",
  headerName: "Description",
  sortable: false,
  filterable: false,
  valueGetter: ({ row }: { row: CurbsuiteTransactionLogEntry }) =>
    isAdjustment(row) ? getAdjustmentDescription(row) ?? "" : "",
} as GridColDef<CurbsuiteTransactionLogEntry>;

// Only for tables with no on-screen Type column (standard park, airport Monthly Charges), so an
// adjustment row is still labelled as one in the CSV.
const EXPORT_TYPE_COLUMN = {
  field: "exportTransactionType",
  headerName: "Type",
  sortable: false,
  filterable: false,
  valueGetter: ({ row }: { row: CurbsuiteTransactionLogEntry }) => {
    if (isAdjustment(row)) {
      return ADJUSTMENT_TYPE_LABEL;
    }
    if (row.transactionType === "park") {
      return "Park";
    }
    const gatewayType = row.transaction?.gatewayTransactions?.[0]?.gatewayTransactionType;
    return (gatewayType && WALLET_TRANSACTION_TYPE_LABELS[gatewayType]) || "";
  },
} as GridColDef<CurbsuiteTransactionLogEntry>;

// Philly Bus's combined Event Start/End export format, matching Duration's HH:MM:SS style
// (PRO-2228).
const PARK_EVENT_DATE_TIME_FORMAT = "MM/dd/yyyy hh:mm:ss a";

// Local clock time of the billable event, export-only. Airports need it because a trip charge has
// no Session Period to expand into start/end/duration -- the trip is an instant, and the on-screen
// Date column only carries the day (AUT-5389, requested by product for the finance export).
const TRIP_TIME_LOCAL_COLUMN = {
  field: "tripTimeLocal",
  headerName: "Time",
  sortable: false,
  filterable: false,
  valueGetter: ({ row }: { row: SessionTimeRow }) => getSessionStartDate(row),
  valueFormatter: (params) => convertTimeToString(params.value as Date),
} as GridColDef<CurbsuiteTransactionLogEntry>;

const PARK_DURATION_COLUMN = {
  field: "parkDuration",
  headerName: "Duration",
  sortable: false,
  filterable: false,
  valueGetter({ row }: { row: CurbsuiteTransactionLogEntry }) {
    if (row.transactionType === "gateway_transaction" || isAdjustment(row)) {
      return "";
    }
    const durationMs = getSessionEndDate(row).getTime() - getSessionStartDate(row).getTime();
    if (durationMs <= 0) {
      return "";
    }
    return formatDurationMs(durationMs);
  },
} as GridColDef<CurbsuiteTransactionLogEntry>;

// Replaces the combined "timeRange" (Session Period) column with export-only columns
// (AUT-5282, AUT-4870) without altering the on-screen TABLE_COLUMNS passed in.
//
// `includeTripTime` splices the local event time in after Date instead, for clients whose billable
// event has no session to expand (airports).
//
// `isBusStation` combines Event Start/End into single Month/Day/Year HH:MM:SS fields, matching
// Duration's HH:MM:SS style (PRO-2228, requested for the Philly Bus CurbPass export only) -- every
// other "park" payee keeps the separate Date/Time columns.
//
// `includeAdjustmentColumns` (AUT-6163, every table that can show adjustments) adds export-only
// "Reference #" and "Description" columns just before Amount -- on screen both sit in the
// adjustment's two-line Type cell -- plus an export-only "Type" where the table has no Type column.
export function buildExportColumns(
  tableColumns: GridColDef[],
  {
    includeTripTime = false,
    vehicleTypeAfterOperator = false,
    isBusStation = false,
    includeAdjustmentColumns = false,
  }: {
    includeTripTime?: boolean;
    vehicleTypeAfterOperator?: boolean;
    isBusStation?: boolean;
    includeAdjustmentColumns?: boolean;
  } = {},
): GridColDef[] {
  const hasTypeColumn = tableColumns.some((col) => TYPE_COLUMN_FIELDS.includes(col.field));
  return tableColumns.flatMap((col) => {
    if (includeAdjustmentColumns && col.field === "amount") {
      return [
        ...(hasTypeColumn ? [] : [EXPORT_TYPE_COLUMN]),
        ADJUSTMENT_REFERENCE_COLUMN,
        ADJUSTMENT_DESCRIPTION_COLUMN,
        col,
      ];
    }
    if (includeTripTime && col.field === "transactionDate") {
      return [col, TRIP_TIME_LOCAL_COLUMN];
    }
    // Airports drop Type from the grid, and product asked for Vehicle Type in its place in the CSV
    // -- the class is what explains an $8 charge versus a $3 one.
    if (vehicleTypeAfterOperator && col.field === "operator") {
      return [col, AIRPORT_TRIP_FEE_AMOUNT_COLUMN];
    }
    if (col.field !== "timeRange") {
      return [col];
    }
    if (isBusStation) {
      return [
        {
          field: "eventStart",
          headerName: "Event Start",
          sortable: false,
          filterable: false,
          valueGetter: ({ row }: { row: ExportSessionRow }) => exportSessionStart(row),
          valueFormatter: formatOrBlank((date) => formatDate(date, PARK_EVENT_DATE_TIME_FORMAT)),
        } as GridColDef<CurbsuiteTransactionLogEntry>,
        {
          field: "eventEnd",
          headerName: "Event End",
          sortable: false,
          filterable: false,
          valueGetter: ({ row }: { row: ExportSessionRow }) => exportSessionEnd(row),
          valueFormatter: formatOrBlank((date) => formatDate(date, PARK_EVENT_DATE_TIME_FORMAT)),
        } as GridColDef<CurbsuiteTransactionLogEntry>,
        PARK_DURATION_COLUMN,
      ];
    }
    return [
      {
        field: "eventStartDate",
        headerName: "Event Start Date",
        sortable: false,
        filterable: false,
        valueGetter: ({ row }: { row: ExportSessionRow }) => exportSessionStart(row),
        valueFormatter: formatOrBlank(convertDateToString),
      } as GridColDef<CurbsuiteTransactionLogEntry>,
      {
        field: "eventStartTime",
        headerName: "Event Start Time",
        sortable: false,
        filterable: false,
        valueGetter: ({ row }: { row: ExportSessionRow }) => exportSessionStart(row),
        valueFormatter: formatOrBlank(convertTimeToString),
      } as GridColDef<CurbsuiteTransactionLogEntry>,
      {
        field: "eventEndDate",
        headerName: "Event End Date",
        sortable: false,
        filterable: false,
        valueGetter: ({ row }: { row: ExportSessionRow }) => exportSessionEnd(row),
        valueFormatter: formatOrBlank(convertDateToString),
      } as GridColDef<CurbsuiteTransactionLogEntry>,
      {
        field: "eventEndTime",
        headerName: "Event End Time",
        sortable: false,
        filterable: false,
        valueGetter: ({ row }: { row: ExportSessionRow }) => exportSessionEnd(row),
        valueFormatter: formatOrBlank(convertTimeToString),
      } as GridColDef<CurbsuiteTransactionLogEntry>,
      PARK_DURATION_COLUMN,
    ];
  });
}
